import { Controller, useFormContext } from "react-hook-form";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import { useResponsive } from "../../hooks/customUseResponsive";
import ExpandMore from "@mui/icons-material/ExpandMore";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

// ----------------------------------------------------------------------
export function RHFSelect({
  name,
  native,
  maxHeight = 220,
  helperText,
  children,
  PaperPropsSx,
  ...other
}) {
  const { control } = useFormContext();
  const isMobile = useResponsive("down", "sm");

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          select
          fullWidth
          value={field.value || ""}
          onChange={(e) => field.onChange(e.target.value)}
          sx={{
            "& .MuiInputBase-root": {
              height: isMobile ? "30px !important" : "40px !important",
              backgroundColor: "#fff",
              fontSize: isMobile ? "12px" : "",
            },
          }}
          SelectProps={{
            native,
            MenuProps: {
              PaperProps: {
                sx: {
                  ...(!native && {
                    maxHeight:
                      typeof maxHeight === "number" ? maxHeight : "unset",
                  }),
                  ...PaperPropsSx,
                },
              },
            },
            sx: { textTransform: "capitalize" },
          }}
          error={!!error}
          helperText={error ? error?.message : helperText}
          {...other}
        >
          {children}
        </TextField>
      )}
    />
  );
}

// ----------------------------------------------------------------------

export function RHFMultiSelect({
  name,
  chip,
  label,
  options,
  checkbox,
  placeholder,
  helperText,
  ...other
}) {
  const { control } = useFormContext();

  const renderValues = (selectedIds) => {
    const selectedItems = options.filter((item) =>
      selectedIds.includes(item.value)
    );

    if (!selectedItems.length && placeholder) {
      return <Box sx={{ color: "text.disabled" }}>{placeholder}</Box>;
    }

    if (chip) {
      return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {selectedItems.map((item) => (
            <Chip key={item.value} size="small" label={item.label} />
          ))}
        </Box>
      );
    }

    return selectedItems.map((item) => item.label).join(", ");
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl error={!!error} {...other}>
          {label && <InputLabel id={name}> {label} </InputLabel>}

          <Select
            {...field}
            multiple
            displayEmpty={!!placeholder}
            id={`multiple-${name}`}
            labelId={name}
            label={label}
            renderValue={renderValues}
          >
            {options.map((option) => {
              const selected = field.value.includes(option.value);

              return (
                <MenuItem key={option.value} value={option.value}>
                  {checkbox && (
                    <Checkbox size="small" disableRipple checked={selected} />
                  )}

                  {option.label}
                </MenuItem>
              );
            })}
          </Select>

          {(!!error || helperText) && (
            <FormHelperText error={!!error}>
              {error ? error?.message : helperText}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}

// ----------------------------------------------------------------------

export function RHFDropdownSelect({
  name,
  helperText,
  children,
  buttonLabel,
  isTimer = false,
  width = 150,
  ...other
}) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        // Check if the button label indicates active filters
        const hasActiveFilters =
          buttonLabel &&
          (buttonLabel.includes("selected") ||
            buttonLabel.includes("All") ||
            (buttonLabel !== "Status" && buttonLabel !== "Avg. Working Hours"));

        return (
          <FormControl error={!!error} sx={{ minWidth: 120, maxWidth: 200 }}>
            <Select
              {...field}
              value={field.value || ""}
              onChange={(e) => field.onChange(e.target.value)}
              displayEmpty
              sx={{
                borderRadius: 5,
                textTransform: "capitalize",
                backgroundColor: hasActiveFilters ? "#e3f2fd" : "transparent",
                borderColor: hasActiveFilters ? "#2196f3" : undefined,
                color: hasActiveFilters ? "#1976d2" : undefined,
                "&:hover": {
                  backgroundColor: hasActiveFilters ? "#bbdefb" : undefined,
                  borderColor: hasActiveFilters ? "#1976d2" : undefined,
                },
                minWidth: 120,
                maxWidth: 200,
                height: 40,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                "& .MuiSelect-select": {
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  paddingRight: "0 !important",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: hasActiveFilters ? "#2196f3" : undefined,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: hasActiveFilters ? "#1976d2" : undefined,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: hasActiveFilters ? "#1976d2" : undefined,
                },
                // Hide the native select icon
                "& .MuiSelect-icon": {
                  display: "none",
                },
                "& .MuiSelect-iconOpen": {
                  display: "none",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    width: width,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                    borderRadius: 2,
                    border: "1px solid rgba(0,0,0,0.12)",
                    mt: 1,
                  },
                },
              }}
              renderValue={(value) => (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 0.5,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {buttonLabel || "Select"}
                  </span>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      ml: 0.5,
                      flexShrink: 0,
                    }}
                  >
                    {isTimer ? <AccessTimeIcon /> : <ExpandMore />}
                  </Box>
                </Box>
              )}
              {...other}
            >
              {children}
            </Select>

            {(!!error || helperText) && (
              <FormHelperText error={!!error} sx={{ mx: 0 }}>
                {error ? error?.message : helperText}
              </FormHelperText>
            )}
          </FormControl>
        );
      }}
    />
  );
}
